import React, { useState, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { forgotPassword } from "../api";
import { ThemeContext } from "../App";
import logo from "../assets/astratrade-logo.svg";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastEmail, setLastEmail] = useState("");
  const { theme, toggleTheme } = useContext(ThemeContext);

  const classes = useMemo(() => {
    const baseBackground =
      theme === "dark"
        ? "bg-slate-950 text-white"
        : "bg-slate-50 text-gray-900";
    const card =
      theme === "dark"
        ? "bg-gray-800 text-white"
        : "bg-white text-gray-900 border border-gray-200";
    const input =
      theme === "dark"
        ? "w-full p-2 mb-4 rounded bg-gray-900 border border-gray-600 text-white placeholder-gray-400"
        : "w-full p-2 mb-4 rounded bg-white border border-gray-300 text-gray-900 placeholder-gray-500";
    const successBox =
      theme === "dark"
        ? "bg-emerald-700 text-white"
        : "bg-emerald-100 text-emerald-800";
    const errorBox =
      theme === "dark" ? "bg-red-700 text-white" : "bg-red-100 text-red-800";
    return { baseBackground, card, input, successBox, errorBox };
  }, [theme]);

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setStatus({ type: "error", message: "Enter your registered email" });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus({ type: "error", message: "Enter a valid email address" });
      return;
    }

    try {
      setLoading(true);
      setStatus(null);
      const target = trimmed.toLowerCase();
      await forgotPassword(target);
      setLastEmail(target);
      setStatus({
        type: "success",
        message:
          "If that email is registered, we've sent a one-time password. Enter the code on the reset page to choose a new password.",
      });
    } catch (err) {
      console.error("Forgot password error", err);
      setStatus({
        type: "error",
        message: "Unable to submit reset request. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div
      className={`flex justify-center items-center min-h-screen transition-colors duration-200 ${classes.baseBackground}`}
    >
      <div
        className={`p-8 rounded-lg shadow-lg w-96 transition-colors duration-200 ${classes.card}`}
      >
        <div className="flex justify-center mb-6">
          <img src={logo} alt="AstraTrade logo" className="h-16 w-auto" />
        </div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Forgot Password</h2>
          <button
            className="text-sm px-3 py-1 rounded border border-current"
            onClick={toggleTheme}
          >
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        {status && (
          <div
            className={`${
              status.type === "error" ? classes.errorBox : classes.successBox
            } p-2 rounded mb-4 text-center`}
          >
            {status.message}
          </div>
        )}

        <input
          type="email"
          placeholder="Registered email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          className={classes.input}
        />

        <button
          className="w-full bg-blue-500 p-2 rounded font-bold hover:bg-blue-600 text-white mb-2 disabled:opacity-60"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Sending..." : "Send OTP"}
        </button>

        {status?.type === "success" && lastEmail && (
          <div className="text-center text-sm mb-2">
            <Link
              to={`/reset-password?email=${encodeURIComponent(lastEmail)}`}
              className="text-blue-500 hover:underline"
            >
              Go to Reset Password
            </Link>
          </div>
        )}

        <div className="text-center">
          <Link to="/" className="text-blue-500 hover:underline">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
